import { useCallback, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { Avatar, Badge, Button } from "@primitives/index";
import { SettingsGroup, SettingsRow } from "@compositions/index";
import { useAuth } from "../auth/useAuth";
import { useCurrentUser } from "../identity";
import { SettingsPage } from "./SettingsPage";

/**
 * Account: who is signed in, the admin entry point, and sign out.
 *
 * Name and email come from the MSAL account. The Admin row appears only when
 * the API confirms the Einstein.Admin role; the admin pages re-check server
 * side, so this is presentation, not authorization.
 */
export function AccountSettingsPage(): ReactElement {
    const auth = useAuth();
    const navigate = useNavigate();
    const currentUser = useCurrentUser();

    const name = auth.account?.name ?? "User";
    const email = auth.account?.username ?? "";
    const isAdmin = currentUser.data?.isAdmin === true;

    const handleOpenAdmin = useCallback(() => void navigate("/queues"), [navigate]);
    const handleSignOut = useCallback(() => {
        void auth.signOut();
    }, [auth]);

    return (
        <SettingsPage title="Account" description="The identity signed in on this device.">
            <SettingsGroup title="Signed in as">
                <SettingsRow
                    variant="readonly"
                    leading={<Avatar alt={name} size="md" />}
                    label={name}
                    description={email}
                    value={
                        isAdmin ? (
                            <Badge variant="accent" data-testid="settings-admin-badge">
                                Admin
                            </Badge>
                        ) : undefined
                    }
                    testId="settings-identity"
                />
            </SettingsGroup>

            <SettingsGroup title="Actions">
                {isAdmin && (
                    <SettingsRow
                        variant="navigation"
                        label="Admin"
                        description="Queues, management groups, and routing."
                        onSelect={handleOpenAdmin}
                        testId="settings-admin"
                    />
                )}
                <SettingsRow
                    variant="action"
                    label="Sign out"
                    description="Ends the session on this device."
                >
                    <Button variant="secondary" size="sm" onClick={handleSignOut}>
                        <LogOut size={14} strokeWidth={2.25} aria-hidden="true" />
                        Sign out
                    </Button>
                </SettingsRow>
            </SettingsGroup>
        </SettingsPage>
    );
}
